"use client";

import { 
  LineChart, 
  BarChart3, 
  ShieldCheck, 
  FileText, 
  Star, 
  MessagesSquare,
  UserRound,
  Home,
  BadgePercent,
  Lightbulb,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/components/patterns/hooks/use-mobile";

const WhyUseRentWise = () => {
  const isMobile = useIsMobile();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5 }
    }
  };

  const statsCards = [
    {
      title: "Verified Reviews",
      icon: Star,
      value: "4,320+",
      description: "Authentic tenant reviews",
      color: "yellow",
    },
    {
      title: "Landlord Ratings",
      icon: UserRound,
      value: "1,250+",
      description: "Rated property owners",
      color: "green",
    },
    {
      title: "Properties",
      icon: Home,
      value: "8,750+",
      description: "Properties in our database",
      color: "blue",
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-rentwise-blue-50 relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.4, scale: 1 }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
        className="absolute top-0 right-0 h-96 w-96 bg-rentwise-blue-100 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.4, scale: 1 }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse", delay: 0.5 }}
        className="absolute bottom-0 left-0 h-96 w-96 bg-rentwise-blue-200 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"
      />
      
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          variants={fadeInUpVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 drop-shadow-sm">
            Why Choose RentWise?
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Experience the power of community-driven insights that help you make smarter rental decisions.
          </p>
        </motion.div>
        
        <div className="max-w-7xl mx-auto">
          {isMobile ? (
            // Mobile accordion view
            <div className="space-y-6">
              {statsCards.map((card, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.2 }}
                >
                  <Card className={`bg-gradient-to-br from-${card.color}-50 to-white transform transition-all duration-300 hover:scale-105 hover:shadow-lg`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold">{card.title}</h3>
                        {(() => { const IconComponent = card.icon; return <IconComponent className={`h-7 w-7 text-${card.color}-500`} />; })()}
                      </div>
                      <p className="text-4xl font-bold text-rentwise-blue-600 mb-2">{card.value}</p>
                      <p className="text-sm text-gray-600">{card.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              <AnimatePresence>
                {/* Dashboard Section */}
                <motion.div
                  key="dashboard-section"
                  initial={false}
                  animate={{ height: activeAccordion === 'dashboard' ? 'auto' : 48 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  <button 
                    onClick={() => toggleAccordion('dashboard')}
                    className="w-full p-4 flex justify-between items-center font-semibold text-left text-lg bg-gradient-to-r from-rentwise-blue-50 to-white"
                  >
                    <span className="flex items-center gap-2">
                      <LineChart className="h-5 w-5 text-rentwise-blue-500" />
                      Dashboard Overview
                    </span>
                    <motion.div
                      key="dashboard-chevron"
                      animate={{ rotate: activeAccordion === 'dashboard' ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="h-5 w-5" />
                    </motion.div>
                  </button>
                  
                  {activeAccordion === 'dashboard' && (
                    <motion.div
                      key="dashboard-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-6 space-y-6"
                    >
                      {/* Dashboard content */}
                      <div className="grid grid-cols-1 gap-6">
                        <div className="bg-gray-50 rounded-xl p-6">
                          <h4 className="text-lg font-semibold mb-4">Recent Rent Trends</h4>
                          <div className="h-48 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100 p-4">
                            {/* Enhanced mobile chart */}
                            <div className="w-full h-full relative">
                              <div className="flex items-end justify-around h-full">
                                {[
                                  { height: 65, label: 'Jan' },
                                  { height: 80, label: 'Feb' },
                                  { height: 55, label: 'Mar' },
                                  { height: 90, label: 'Apr' },
                                  { height: 70, label: 'May' },
                                  { height: 85, label: 'Jun' },
                                  { height: 75, label: 'Jul' }
                                ].map((data, i) => (
                                  <div key={`mobile-chart-${i}`} className="flex flex-col items-center">
                                    <motion.div
                                      initial={{ height: 0 }}
                                      animate={{ height: `${data.height}%` }}
                                      transition={{ duration: 0.5, delay: i * 0.1 }}
                                      className="w-4 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg"
                                    />
                                    <span className="text-xs text-gray-500 mt-1">{data.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>

                {/* Features Section */}
                <motion.div
                  key="features-section"
                  initial={false}
                  animate={{ height: activeAccordion === 'features' ? 'auto' : 48 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden mt-6"
                >
                  <button 
                    onClick={() => toggleAccordion('features')}
                    className="w-full p-4 flex justify-between items-center font-semibold text-left text-lg bg-gradient-to-r from-rentwise-blue-50 to-white"
                  >
                    <span className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-rentwise-blue-500" />
                      Feature Comparison
                    </span>
                    <motion.div
                      key="features-chevron"
                      animate={{ rotate: activeAccordion === 'features' ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="h-5 w-5" />
                    </motion.div>
                  </button>
                  
                  {activeAccordion === 'features' && (
                    <motion.div
                      key="features-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-6"
                    >
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50%]">Feature</TableHead>
                            <TableHead className="text-center">RentWise</TableHead>
                            <TableHead className="text-center">Others</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            "Verified Reviews",
                            "Landlord Ratings",
                            "Price History",
                            "Community Insights",
                            "Rental Score"
                          ].map((feature, index) => (
                            <motion.tr
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="border-b"
                            >
                              <TableCell className="font-medium">{feature}</TableCell>
                              <TableCell className="text-center text-green-600">✓</TableCell>
                              <TableCell className="text-center text-red-500">✗</TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          ) : (
            // Desktop view
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12"
            >
              {/* Left column - Stats and Chart */}
              <div className="space-y-8">
                <div className="grid grid-cols-2 gap-6">
                  {statsCards.slice(0, 2).map((card, index) => (
                    <motion.div
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.2 }}
                      whileHover={{ scale: 1.05 }}
                      className="col-span-1"
                    >
                      <Card className={`bg-gradient-to-br from-${card.color}-50 to-white`}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">{card.title}</h3>
                            {(() => { const IconComponent = card.icon; return <IconComponent className={`h-7 w-7 text-${card.color}-500`} />; })()}
                          </div>
                          <p className="text-4xl font-bold text-rentwise-blue-600 mb-2">{card.value}</p>
                          <p className="text-sm text-gray-600">{card.description}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
                
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl shadow-lg p-6"
                >
                  <h3 className="text-xl font-semibold mb-4">Rental Market Trends</h3>
                  <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                    {/* Enhanced chart with trend lines and labels */}
                    <div className="w-full h-full relative">
                      <div className="flex items-end justify-around h-full">
                        {[
                          { height: 65, label: 'Jan', value: '₦650k' },
                          { height: 80, label: 'Feb', value: '₦800k' },
                          { height: 55, label: 'Mar', value: '₦550k' },
                          { height: 90, label: 'Apr', value: '₦900k' },
                          { height: 70, label: 'May', value: '₦700k' },
                          { height: 85, label: 'Jun', value: '₦850k' },
                          { height: 75, label: 'Jul', value: '₦750k' }
                        ].map((data, i) => (
                          <div key={`desktop-chart-${i}`} className="flex flex-col items-center">
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: `${data.height}%` }}
                              transition={{ duration: 0.5, delay: i * 0.1 }}
                              className="w-8 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg relative group"
                            >
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs px-2 py-1 rounded">
                                {data.value}
                              </div>
                            </motion.div>
                            <span className="text-xs text-gray-500 mt-2">{data.label}</span>
                          </div>
                        ))}
                      </div>
                      {/* Trend line overlay */}
                      <div className="absolute inset-0 pointer-events-none">
                        <svg className="w-full h-full">
                          <motion.path
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 2, delay: 0.8 }}
                            d="M 10,160 Q 50,120 90,180 T 170,100 T 250,130 T 330,120"
                            stroke="rgb(59, 130, 246)"
                            strokeWidth="2"
                            fill="none"
                            strokeDasharray="4 4"
                            opacity="0.6"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Right column - Feature comparison */}
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.6 }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <h3 className="text-xl font-semibold mb-6">Why RentWise Stands Out</h3>
                <div className="space-y-6">
                  {[
                    {
                      icon: ShieldCheck,
                      title: "Verified Reviews",
                      description: "Every review is authenticated and verified"
                    },
                    {
                      icon: LineChart,
                      title: "Price History",
                      description: "Track rental price changes over time"
                    },
                    {
                      icon: MessagesSquare,
                      title: "Community Insights",
                      description: "Real experiences from actual tenants"
                    }
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className="flex items-start gap-4 p-4 rounded-lg bg-[var(--color-tenant-primary)]/5"
                    >
                      <feature.icon className="h-6 w-6 text-[var(--color-tenant-primary)] mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">{feature.title}</h4>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhyUseRentWise;
